import crypto from 'crypto';
import {
  API_TYPE,
  SH_CLIENT_ID,
  SH_DASHBOARD,
  SH_EO_BROWSER_SANDBOX_TUTORIAL,
  SH_GRANT_TYPE,
  SH_KEYCLOAK_OAUTH,
} from '@account/config';
import {Routes} from '@account/utils/route';
import {trackEvent} from '@account/analytics';
import {v4 as uuidv4} from 'uuid';

export const LINK_DUPLICATE_ACCOUNT_FAILED_ERROR =
  'linkDuplicateAccountFailedError';

export const OAUTH_STATE_KEY = `link-sentinel-hub-state-${API_TYPE}-oauth`;

export const getLinkSentinelHubStateKey = id => {
  return `link-sentinel-hub-state-${API_TYPE}-${id ?? 'undefined'}`;
};

const getState = key => {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch {
    return {};
  }
};

const setState = (key, state) => {
  if (
    !state ||
    (typeof state === 'object' && Object.keys(state).length === 0)
  ) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(state));
};

export const getLinkState = id => {
  return getState(getLinkSentinelHubStateKey(id));
};

export const setLinkState = (id, state) => {
  setState(getLinkSentinelHubStateKey(id), state);
};

export const getOAuthState = () => {
  return getState(OAUTH_STATE_KEY);
};

export const setOAuthState = state => {
  setState(OAUTH_STATE_KEY, state);
};

const replaceNonUrlCharacters = str => {
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64UrlEncode = plain => {
  return replaceNonUrlCharacters(plain.toString('base64'));
};

export const generateCodeVerifier = async () => {
  if (window?.crypto?.subtle) {
    // Use the browser's crypto
    return Array.from(
      window.crypto.getRandomValues(new Uint8Array(64 / 2)),
      dec => ('0' + dec.toString(16)).substr(-2)
    ).join('');
  } else {
    return base64UrlEncode(crypto.randomBytes(32));
  }
};

export const generateCodeChallenge = async codeVerifier => {
  const data = new TextEncoder().encode(codeVerifier);

  let hashed = '';
  if (window?.crypto?.subtle) {
    // Use the browser's crypto
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    hashed = String.fromCharCode(...new Uint8Array(hash));
    return replaceNonUrlCharacters(btoa(hashed));
  } else {
    hashed = crypto.createHash('sha256').update(data).digest('base64');
    return base64UrlEncode(hashed);
  }
};

export const getPath = ({
  href = window.location.href,
  hash = '',
  pathname = '/',
  params = {},
}) => {
  const newUrl = new URL(href);
  newUrl.pathname = pathname;
  newUrl.hash = hash;
  if (Object.keys(params).length > 0) {
    newUrl.search = new URLSearchParams(params).toString();
  }
  return newUrl.href;
};

export const getPathname = url =>
  (url ?? new URL(window.location.href)).pathname.replace(/\/$/, '');

export const getSentinelHubOAuthUrl = async ({
  path,
  clientId,
  origin,
  params = {},
  redirectParams = {},
}) => {
  const state = uuidv4();
  const verifier = await generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const redirectUri = getPath({
    pathname: getPathname() + Routes.SENTINEL_HUB_CALLBACK,
    params: {...redirectParams, ...(origin ? {origin} : {})},
  });

  const paramsList = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'openid email profile',
    response_type: 'code',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    kc_idp_hint: 'planet-account-link',
    ...params,
  });

  return {
    url: `${path}?${paramsList.toString()}`,
    state,
    verifier,
    challenge,
    redirectUri,
  };
};

export const formTokenPayload = (authCode, codeVerifier, redirectUri) => ({
  grant_type: SH_GRANT_TYPE,
  code: authCode,
  client_id: SH_CLIENT_ID,
  redirect_uri: redirectUri,
  code_verifier: codeVerifier,
});

const removeOAuthValuesFromURL = urlString => {
  const userSettingsURL = new URL(urlString);

  userSettingsURL.searchParams.delete('code');
  userSettingsURL.searchParams.delete('state');
  userSettingsURL.searchParams.delete('session_state');

  return userSettingsURL.href;
};

export const isSentinelHubCallback = () => {
  return window.location.pathname.endsWith(Routes.SENTINEL_HUB_CALLBACK);
};

const waitRenew = async authClient => {
  await authClient.renew();

  // authClient.renew() resolves when the token renewal is initiated, not finished, so add additional wait time
  await new Promise(resolve => setTimeout(resolve, 3000));
};

export const getOriginQueryParam = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('origin') || 'email';
};

/**
 * @param {import('@apps-common/auth').Client} authClient The auth client.
 * @param {Function} renderCallbackPage The function to render the redirect page.
 */
export async function handleSentinelHubCallback(
  authClient,
  renderCallbackPage
) {
  const plUser = authClient.getState().user;
  // We observed that some Keycloak navigates the browser to redirect uris
  // that have a hash instead of a & when providing query parameters.
  // This seems to be automatically handled in Chrome, but not in Safari.
  const cleanUrl = new URL(window.location.href.replace(/#/g, '&'));

  if (cleanUrl.searchParams.get('new_account') === 'true') {
    renderCallbackPage('Redirecting to EOBrowser...');

    // Before we redirect to EO Browser, we need to ensure the user's token is renewed
    // When they return to Account, the UI will be updated as a result.
    await waitRenew(authClient);

    // event for new account redirecting to EO Browser
    trackEvent(
      'Sentinel Hub: Display EOBrowser with Planet Sandbox Tutorial open',
      {
        origin: getOriginQueryParam(),
        sh_user_id: plUser?.claims?.sh_user_id,
      }
    );

    window.location.href = SH_EO_BROWSER_SANDBOX_TUTORIAL;

    return;
  }

  renderCallbackPage('Linking...');

  await waitRenew(authClient);
  const userSettingsUrl = getPath({
    pathname: getPathname(cleanUrl).replace(Routes.SENTINEL_HUB_CALLBACK, '/'),
    hash: Routes.USER_SETTINGS,
    params: cleanUrl.searchParams,
  });

  // ensure code/state/session_state are removed from URL params
  window.location.href = removeOAuthValuesFromURL(userSettingsUrl);
}

export const openLinkingFlow = async origin => {
  const {url} = await getSentinelHubOAuthUrl({
    path: SH_KEYCLOAK_OAUTH,
    clientId: SH_CLIENT_ID,
    origin,
    params: {
      response_mode: 'fragment',
      prompt: 'login',
    },
    redirectParams: {
      isLinking: 'true',
    },
  });

  window.location.href = url;
};

export const hasSentinelHubUser = user => !!user?.sh_user_id;

const getPUConsumedValue = response =>
  response?.consumed?.processing_units_value;

const getPUTotalValue = response => response?.total?.processing_units_value;

const getRequestsConsumedValue = response => response?.consumed?.requests_value;

const getRequestsTotalValue = response => response?.total?.requests_value;

export const parseSentinelHubUsageIntoPlan = response => {
  if (!response) {
    return {};
  }
  const parsedResponse = {
    id: 'sentinel_hub_usage',
    product_type: 'sentinel_hub',
    active_from: null,
    active_to: response.valid_to,
    organization: {},
    plan: {
      name: `Sentinel Hub ${response.account_type}`,
    },
    pl_number: response.sh_account_id,
    status: null,
    quotas: {},
    quota_units: response.quota_units,
    external_url: SH_DASHBOARD,
    caption:
      'Sentinel Hub usage is calculated per individual rather than per organization',
  };

  parsedResponse.quotas.sh_pu_quota = {
    id: 'sentinelhubPUQuota',
    key: 'sentinelhubPUQuota',
    limit: getPUTotalValue(response),
    current: getPUConsumedValue(response),
    product_type: 'PROCESSING UNITS',
    enabled: true,
    metering: '',
  };

  parsedResponse.quotas.sh_requests_quota = {
    id: 'sentinelhubRequestsQuota',
    key: 'sentinelhubRequestsQuota',
    limit: getRequestsTotalValue(response),
    current: getRequestsConsumedValue(response),
    product_type: 'REQUESTS',
    enabled: true,
    metering: '',
  };

  return parsedResponse;
};
